import { useState, useEffect, useCallback } from "react";
import { useDependency } from "../../contexts/DependencyProvider";
import { ListMailboxRequest } from "../../services/soap/mail/requests/ListMailboxRequest";
import { GetMailboxRequest } from "../../services/soap/mail/requests/GetMailboxRequest";
import { ListMessageTypeRequest } from "../../services/soap/message/requests/ListMessageTypeRequest";
import moment from "moment";
import { NewMessageFormData } from "./useNewMessageForm";
import { useReefConfig } from "../UseReefConfig";
import { joinPaths } from "../../utils/helperUtil";

/**
 * Supported message types
 * 1 = Note, 6 = Phone, 7 = Email, 12 = Meeting
 */
export const SUPPORTED_MESSAGE_TYPES = ["1", "6", "7", "12"] as const;
export type SupportedMessageType = (typeof SUPPORTED_MESSAGE_TYPES)[number];

/**
 * Mailbox option interface
 */
export interface MailboxOption {
  id: string;
  name: string;
}

/**
 * Message type option interface
 */
export interface MessageTypeOption {
  id: string;
  text: string;
  type: string;
  priority: string;
}

/**
 * Communication settings returned by the hook
 */
export interface CommunicationSettings {
  mailboxes: MailboxOption[];
  messageTypes: MessageTypeOption[];
  defaultMailbox: MailboxOption | null;
  mailboxSignature: string;
  defaultMessageType: MessageTypeOption | null;
}

interface UseNewMessageSettingsOptions {
  /** Session object containing user preferences */
  session: any;
  /** Optional message object for reply scenarios */
  messageObj?: any;
  /** Reply type: 'REPLY' | 'REPLYALL' | undefined */
  replyType?: string;
  /** Callback when settings are loaded */
  onSettingsLoaded?: (
    settings: CommunicationSettings,
    defaultFormFields: Partial<NewMessageFormData>,
  ) => void;
  /** Callback on error */
  onError?: (error: any) => void;
}

/**
 * Format signature text for HTML display
 */
export const formatSignature = (signature: string): string => {
  return signature ? "<br/>" + signature.replace(/\n/g, "<br/>") : "";
};

/**
 * Parse email strings into structured objects
 */
const splitEmailList = (value: string): string[] => {
  const entries: string[] = [];
  let current = "";
  let inQuotes = false;
  let inAngle = false;

  for (const char of value) {
    if (char === '"') {
      inQuotes = !inQuotes;
      current += char;
      continue;
    }

    if (char === "<" && !inQuotes) {
      inAngle = true;
      current += char;
      continue;
    }

    if (char === ">" && !inQuotes) {
      inAngle = false;
      current += char;
      continue;
    }

    if ((char === "," || char === ";") && !inQuotes && !inAngle) {
      const trimmed = current.trim();
      if (trimmed) entries.push(trimmed);
      current = "";
      continue;
    }

    current += char;
  }

  const trimmed = current.trim();
  if (trimmed) entries.push(trimmed);
  return entries;
};

const normalizeEmailEntry = (value: string): any | null => {
  const rawValue = value.trim();
  if (!rawValue) return null;

  const angleMatch = rawValue.match(/^(.*?)<([^>]+)>$/);
  const normalizedEmail = angleMatch
    ? angleMatch[2].trim()
    : rawValue.replace(/^"+|"+$/g, "").trim();

  if (!/@/.test(normalizedEmail)) {
    return null;
  }

  const normalizedName = angleMatch
    ? angleMatch[1].trim().replace(/^"+|"+$/g, "").trim()
    : normalizedEmail;

  return {
    id: normalizedEmail,
    name: normalizedName || normalizedEmail,
    contactName: normalizedName || normalizedEmail,
    emailText: normalizedEmail,
  };
};

export const parseEmails = (emailString: string | string[]): any[] => {
  if (!emailString) return [];

  const emails = Array.isArray(emailString)
    ? emailString
    : splitEmailList(emailString);

  return emails
    .flatMap((email: string) => splitEmailList(email))
    .map(normalizeEmailEntry)
    .filter((entry): entry is any => entry != null);
};

/**
 * Extract the plain email address from a recipient string.
 * Supports values like: Name <email@example.com>
 */
const getEmailIdentity = (value: string): string => {
  const match = value.match(/<([^>]+)>/);
  return (match ? match[1] : value).trim().toLowerCase();
};

const hasEmailLikeValue = (value: string): boolean => {
  return /@/.test(value) || /<[^>]+>/.test(value);
};

/**
 * Deduplicate recipients by their normalized email address.
 */
const dedupeRecipients = (recipients: any[]): any[] => {
  const seen = new Set<string>();

  return recipients.filter((recipient) => {
    const key = getEmailIdentity(
      recipient?.emailText || recipient?.id || recipient?.name || "",
    );
    if (!key || seen.has(key)) return false;
    seen.add(key);
    return true;
  });
};

/**
 * Escape HTML special characters
 */
export const escapeHtml = (str: string): string => {
  return (
    str?.replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;") ||
    ""
  );
};

/**
 * Extract email strings from a recipient sub-field (TO, CC, BCC).
 * Handles: string | string[] | {CONTENT: string} | Array<{CONTENT: string}>
 */
const extractRecipientFieldEmails = (field: any): any[] => {
  if (!field) return [];
  if (Array.isArray(field)) {
    return dedupeRecipients(
      field.flatMap((item: any) => extractRecipientFieldEmails(item)),
    );
  }
  if (typeof field === "string") return parseEmails(field);
  if (typeof field?.CONTENT === "string") return parseEmails(field.CONTENT);
  if (typeof field?.content === "string") return parseEmails(field.content);

  const directValue = [
    field?.emailText,
    field?.email,
    field?.address,
    field?.name,
    field?.id,
  ].find((value) => typeof value === "string" && value.trim().length > 0);

  if (typeof directValue === "string") {
    return parseEmails(directValue);
  }
  return [];
};

const extractNotifyRecipients = (recipient: any): any[] => {
  if (!recipient || typeof recipient !== "object") return [];

  const users = Array.isArray(recipient.USER)
    ? recipient.USER.map((user: any) => ({
        id: user?.id || user?.email || user?.name,
        contactName: user?.contact || user?.name || user?.email || user?.id,
      }))
    : [];

  const notifyEmails = extractRecipientFieldEmails(
    recipient.NOTIFY ?? recipient.notify,
  ).map((item: any) => ({
    id: item.id,
    contactName: item.emailText || item.name || item.id,
  }));

  return users
    .concat(notifyEmails)
    .filter((item: any) => item?.id)
    .filter(
      (item: any, index: number, arr: any[]) =>
        arr.findIndex((candidate: any) => candidate.id === item.id) === index,
    );
};

const extractSenderEmails = (objMessage: any, messageObj: any): any[] => {
  const senderCandidates = [
    objMessage?.replyTo,
    objMessage?.replyTO,
    objMessage?.REPLYTO?.string,
    objMessage?.REPLYTO?.CONTENT,
    messageObj?.replyTo,
    messageObj?.replyTO,
    messageObj?.REPLYTO?.string,
    messageObj?.REPLYTO?.CONTENT,
    objMessage?.mailFrom,
    messageObj?.mailFrom,
    objMessage?.sender,
    messageObj?.sender,
  ].filter((value): value is string => typeof value === "string" && value.trim().length > 0);

  const emailLikeCandidates = senderCandidates.filter(hasEmailLikeValue);
  const preferredCandidates =
    emailLikeCandidates.length > 0 ? emailLikeCandidates : senderCandidates;

  return dedupeRecipients(
    preferredCandidates.flatMap((value) => parseEmails(value)),
  );
};

/**
 * Parse recipients from message object (handles various structures)
 */
export const parseRecipients = (
  objMessage: any,
): { toEmails: any[]; ccEmails: any[] } => {
  const toEmails: any[] = [];
  const ccEmails: any[] = [];

  const recipient = objMessage?.RECIPIENT;

  if (!recipient) {
    toEmails.push(...extractRecipientFieldEmails(objMessage?.TO));
    ccEmails.push(...extractRecipientFieldEmails(objMessage?.CC));
    return {
      toEmails: dedupeRecipients(toEmails),
      ccEmails: dedupeRecipients(ccEmails),
    };
  }

  if (Array.isArray(recipient) || typeof recipient === "string") {
    toEmails.push(...extractRecipientFieldEmails(recipient));
  } else {
    toEmails.push(
      ...extractRecipientFieldEmails(
        recipient.TO ?? recipient.to ?? recipient.To ?? objMessage?.TO,
      ),
    );
    ccEmails.push(
      ...extractRecipientFieldEmails(
        recipient.CC ?? recipient.cc ?? recipient.Cc ?? objMessage?.CC,
      ),
    );

    if (toEmails.length === 0 && ccEmails.length === 0) {
      toEmails.push(
        ...extractRecipientFieldEmails(
          recipient.CONTENT ?? recipient.content ?? recipient,
        ),
      );
    }
  }

  return {
    toEmails: dedupeRecipients(toEmails),
    ccEmails: dedupeRecipients(ccEmails),
  };
};

/**
 * Build reply content with original message header
 */
export const buildReplyContent = (
  objMessage: any,
  toEmails: any[],
  ccEmails: any[],
  signature: string,
): { subject: string; content: string } => {
  const originalSubject =
    objMessage.subject || objMessage.SUBJECT?.string || "";
  const replySubject = originalSubject.toLowerCase().startsWith("re:")
    ? originalSubject
    : `Re: ${originalSubject}`;

  // Build original message header
  const contentDetail =
    `<b>From:</b> ${escapeHtml(
      objMessage.mailFrom || objMessage.sender || "",
    )}<br/>` +
    `<b>Sent:</b> ${
      objMessage.date
        ? moment(objMessage.date).format("DD MMMM YYYY HH:mm")
        : ""
    }<br/>` +
    `<b>To:</b> ${escapeHtml(
      toEmails.map((e: any) => e.emailText).join(", "),
    )}<br/>` +
    (ccEmails.length > 0
      ? `<b>Cc:</b> ${escapeHtml(
          ccEmails.map((e: any) => e.emailText).join(", "),
        )}<br/>`
      : "") +
    `<b>Subject:</b> ${escapeHtml(originalSubject)}<br/><br/>`;

  const originalContent =
    contentDetail +
    (objMessage.PREVIEW?.content ||
      objMessage.BODY?.string ||
      objMessage.teaser?.replace(/\n/g, "<br/>") ||
      "");

  const replyContent = `${signature}<br/><br/>-----Original Message-----<br/><br/>${originalContent}`;

  return { subject: replySubject, content: replyContent };
};

/**
 * Build forward content similar to common email clients.
 * Keeps recipients empty but quotes the original message with a forwarded header.
 */
export const buildForwardContent = (
  objMessage: any,
  toEmails: any[],
  ccEmails: any[],
  signature: string,
  previewHtml?: string,
): { subject: string; content: string } => {
  const originalSubject =
    objMessage.subject || objMessage.SUBJECT?.string || "";
  const forwardSubject =
    originalSubject.toLowerCase().startsWith("fw:") ||
    originalSubject.toLowerCase().startsWith("fwd:")
      ? originalSubject
      : `Fwd: ${originalSubject}`;

  const forwardedHeader =
    `<b>---------- Forwarded message ----------</b><br/>` +
    `<b>From:</b> ${escapeHtml(
      objMessage.mailFrom || objMessage.sender || "",
    )}<br/>` +
    `<b>Date:</b> ${
      objMessage.date
        ? moment(objMessage.date).format("DD MMMM YYYY HH:mm")
        : ""
    }<br/>` +
    `<b>Subject:</b> ${escapeHtml(originalSubject)}<br/>` +
    `<b>To:</b> ${escapeHtml(
      toEmails.map((e: any) => e.emailText).join(", "),
    )}<br/>` +
    (ccEmails.length > 0
      ? `<b>Cc:</b> ${escapeHtml(
          ccEmails.map((e: any) => e.emailText).join(", "),
        )}<br/>`
      : "") +
    `<br/>`;

  const originalContent =
    forwardedHeader +
    (previewHtml ||
      objMessage.PREVIEW?.content ||
      objMessage.BODY?.string ||
      objMessage.teaser?.replace(/\n/g, "<br/>") ||
      "");

  const forwardContent = `${signature}<br/><br/>${originalContent}`;
  return { subject: forwardSubject, content: forwardContent };
};

/**
 * Hook for loading and managing new message communication settings
 * Handles mailbox loading, message types, and reply template population
 */
export const useNewMessageSettings = ({
  session,
  messageObj,
  replyType,
  onSettingsLoaded,
  onError,
}: UseNewMessageSettingsOptions) => {
  const { store, mailService, messageService } = useDependency();
  const { message_preview } = useReefConfig();

  const [isLoading, setIsLoading] = useState(false);
  const [mailboxes, setMailboxes] = useState<MailboxOption[]>([]);
  const [messageTypes, setMessageTypes] = useState<MessageTypeOption[]>([]);
  const [defaultMailbox, setDefaultMailbox] = useState<MailboxOption | null>(
    null,
  );
  const [mailboxSignature, setMailboxSignature] = useState("");
  const [defaultMessageType, setDefaultMessageType] =
    useState<MessageTypeOption | null>(null);
  const [error, setError] = useState<any>(null);
  const [forwardPreviewHtml, setForwardPreviewHtml] = useState<string>("");

  useEffect(() => {
    // For Forward, prefer msg_preview HTML over SOAP PREVIEW/BODY fields.
    if (replyType !== "FORWARD") {
      setForwardPreviewHtml("");
      return;
    }
    const msgId = String(messageObj?.id ?? messageObj?.MESSAGE?.id ?? "").trim();
    const base = String(message_preview?.uri ?? "").trim();
    if (!msgId || !base) {
      setForwardPreviewHtml("");
      return;
    }

    const previewBaseUrl = joinPaths(base, "get_message");
    const query = new URLSearchParams({
      server: String(store.Server ?? ""),
      session: String(store.SessionId ?? ""),
      message_id: msgId,
    });
    const url = `${previewBaseUrl}?${query.toString()}`;

    let cancelled = false;
    (async () => {
      try {
        const res = await fetch(url, { method: "GET" });
        const html = await res.text();
        if (!cancelled) {
          setForwardPreviewHtml(html || "");
        }
      } catch {
        if (!cancelled) {
          setForwardPreviewHtml("");
        }
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [messageObj, message_preview?.uri, replyType, store.Server, store.SessionId]);

  /**
   * Fetch mailbox details and signature
   */
  const fetchMailboxDetails = useCallback(
    async (mailboxId: number): Promise<{ mailbox: any; signature: string }> => {
      try {
        const response = await mailService.getMailbox(
          new GetMailboxRequest(store.Server, store.SessionId, mailboxId),
        );

        const signature = response?.MAILBOX?.signature
          ? formatSignature(response.MAILBOX.signature)
          : "";

        return { mailbox: response?.MAILBOX, signature };
      } catch (err) {
        console.error("Error fetching mailbox details:", err);
        return { mailbox: null, signature: "" };
      }
    },
    [mailService, store.Server, store.SessionId],
  );

  /**
   * Main effect to load communication settings
   */
  useEffect(
    () => {
      const loadSettings = async () => {
        try {
          setIsLoading(true);
          setError(null);

          // Parallel fetch for better performance
          const [mailboxResponse, messageTypesResponse] = await Promise.all([
            mailService.listMailbox(
              new ListMailboxRequest(store.Server, store.SessionId, {
                usableByUser: store.UserId,
                offset: 0,
                limit: 100,
              }),
            ),
            messageService.listMessageType(
              new ListMessageTypeRequest(store.Server, store.SessionId),
            ),
          ]);

          // Process mailboxes
          let availableMailboxes: MailboxOption[] = [];
          let defaultMb: MailboxOption | null = null;
          let signature = "";

          if (
            mailboxResponse?.result === "OK" &&
            mailboxResponse.MAILBOX?.length > 0
          ) {
            availableMailboxes = mailboxResponse.MAILBOX.map(
              (mailbox: any) => ({
                id: mailbox.id,
                name: `${mailbox.name}@${mailbox.domainName}`,
              }),
            );

            // Find default mailbox based on user preferences
            defaultMb =
              availableMailboxes.find(
                (mb) => mb.id === session?.userPreferences?.mailboxId,
              ) ||
              availableMailboxes[0] ||
              null;

            if (defaultMb) {
              const details = await fetchMailboxDetails(Number(defaultMb.id));
              signature = details.signature;
            }
          }

          // Process message types
          let sortedMessageTypes: MessageTypeOption[] = [];
          let defaultMsgType: MessageTypeOption | null = null;

          if (
            messageTypesResponse?.result === "OK" &&
            messageTypesResponse.ITEM
          ) {
            sortedMessageTypes = messageTypesResponse.ITEM.filter((item: any) =>
              SUPPORTED_MESSAGE_TYPES.includes(item.type),
            )
              .sort(
                (a: any, b: any) =>
                  (Number(b.priority) || 0) - (Number(a.priority) || 0),
              )
              .map((item: any) => ({
                id: item.id,
                text: item.name,
                type: item.type,
                priority: item.priority,
              }));

            defaultMsgType = sortedMessageTypes[0] || null;
          }

          // Update local state
          setMailboxes(availableMailboxes);
          setMessageTypes(sortedMessageTypes);
          setDefaultMailbox(defaultMb);
          setMailboxSignature(signature);
          setDefaultMessageType(defaultMsgType);

          // Build default form fields
          const defaultFormFields: Partial<NewMessageFormData> = {
            from: defaultMb?.name || "",
            fromValue: defaultMb,
            mailBox: signature,
            emailContent: signature,
            messageType: defaultMsgType?.type || "",
            messageTypeValue: defaultMsgType
              ? { text: defaultMsgType.text, type: defaultMsgType.type }
              : null,
          };

          // Notify parent component
          if (onSettingsLoaded) {
            const settings: CommunicationSettings = {
              mailboxes: availableMailboxes,
              messageTypes: sortedMessageTypes,
              defaultMailbox: defaultMb,
              mailboxSignature: signature,
              defaultMessageType: defaultMsgType,
            };
            onSettingsLoaded(settings, defaultFormFields);
          }
        } catch (err) {
          console.error("Error loading communication settings:", err);
          setError(err);
          onError?.(err);
        } finally {
          setIsLoading(false);
        }
      };

      loadSettings();
    },
    /* eslint-disable react-hooks/exhaustive-deps */
    [
      store.Server,
      store.SessionId,
      store.UserId,
      session?.userPreferences?.mailboxId,
    ],
  );

  /**
   * Populate reply template when message object and mailbox signature are available
   */
  const populateReplyTemplate =
    useCallback((): Partial<NewMessageFormData> | null => {
      if (!replyType || !messageObj) return null;

      try {
        const objMessage = messageObj.MESSAGE || messageObj;
        // getMessage returns RECIPIENT as a flat string (no TO/CC structure).
        // selectedDataRow (spread into top-level messageObj) carries the full
        // structured {TO, CC} object — prefer that for recipient parsing.
        const recipientSource =
          messageObj.RECIPIENT && typeof messageObj.RECIPIENT === "object"
            ? messageObj
            : objMessage.RECIPIENT && typeof objMessage.RECIPIENT === "object"
              ? objMessage
              : messageObj;
        const { toEmails, ccEmails } = parseRecipients(recipientSource);

        // Parse notify users
        const notifyUsers = extractNotifyRecipients(recipientSource.RECIPIENT);

        const isForward = replyType === "FORWARD";
        const { subject: nextSubject, content: nextContent } = isForward
          ? buildForwardContent(
              objMessage,
              toEmails,
              ccEmails,
              mailboxSignature,
              forwardPreviewHtml,
            )
          : buildReplyContent(objMessage, toEmails, ccEmails, mailboxSignature);

        // Parse sender email for reply
        const senderEmail = extractSenderEmails(objMessage, messageObj);
        const isReplyAll = replyType === "REPLYALL";
        const isOutgoingByMailbox = senderEmail.some((item: any) =>
          [
            ...mailboxes.map((mailbox) => mailbox.name),
            defaultMailbox?.name,
            session?.userPreferences?.mailboxName,
          ]
            .filter(Boolean)
            .map((value) => getEmailIdentity(String(value)))
            .includes(getEmailIdentity(item.emailText)),
        );
        // senderContactId is not a reliable outgoing/incoming signal:
        // incoming external emails can still be linked to a contact and carry it.
        // Treat the message as outgoing only when the sender address matches one
        // of our own mailboxes.
        const isOutgoingMessage = isOutgoingByMailbox;
        const senderKeys = new Set(
          senderEmail.map((item: any) => getEmailIdentity(item.emailText)),
        );
        const mailboxKeys = new Set(
          [
            ...mailboxes.map((item) => item.name),
            defaultMailbox?.name,
            session?.userPreferences?.mailboxName,
          ]
            .filter(Boolean)
            .map((value) => getEmailIdentity(String(value))),
        );
        const contextToRecipients = dedupeRecipients(toEmails).filter(
          (recipient: any) => {
            const key = getEmailIdentity(recipient.emailText);
            return !mailboxKeys.has(key);
          },
        );
        const contextCcRecipients = dedupeRecipients(ccEmails).filter(
          (recipient: any) => {
            const key = getEmailIdentity(recipient.emailText);
            return !mailboxKeys.has(key);
          },
        );
        const replyAllCc = dedupeRecipients([...toEmails, ...ccEmails]).filter(
          (recipient: any) => {
            const key = getEmailIdentity(recipient.emailText);
            return !senderKeys.has(key) && !mailboxKeys.has(key);
          },
        );

        const fallbackIncomingRecipients = dedupeRecipients([
          ...contextToRecipients,
          ...contextCcRecipients,
        ]).filter((recipient: any) => {
          const key = getEmailIdentity(recipient.emailText);
          return !mailboxKeys.has(key);
        });

        console.log("Reply template resolution", {
          replyType,
          rawMessageObj: messageObj,
          objMessage,
          recipientSource,
          parsed: {
            toEmails,
            ccEmails,
            notifyUsers,
            senderEmail,
          },
          derived: {
            isOutgoingMessage,
            isOutgoingByMailbox,
            mailboxKeys: Array.from(mailboxKeys),
            contextToRecipients,
            contextCcRecipients,
            replyAllCc,
            fallbackIncomingRecipients,
          },
        });

        return {
          to: isForward
            ? []
            : isOutgoingMessage
              ? contextToRecipients
              : senderEmail.length > 0
                ? senderEmail
                : fallbackIncomingRecipients,
          cc: isForward
            ? []
            : isOutgoingMessage
              ? isReplyAll
                ? contextCcRecipients
                : []
              : isReplyAll
                ? replyAllCc
                : [],
          notify: isForward ? [] : notifyUsers,
          subject: nextSubject,
          emailContent: nextContent,
          messageType: "7", // Email type
          messageTypeValue: { text: "Email", type: "7" },
        };
      } catch (err) {
        console.error("Error populating reply template:", err);
        return null;
      }
    }, [
      replyType,
      messageObj,
      mailboxSignature,
      mailboxes,
      defaultMailbox,
      session?.userPreferences?.mailboxName,
      forwardPreviewHtml,
    ]);

  /**
   * Change mailbox and update signature
   */
  const changeMailbox = useCallback(
    async (mailboxId: string): Promise<string> => {
      const details = await fetchMailboxDetails(Number(mailboxId));
      setMailboxSignature(details.signature);
      return details.signature;
    },
    [fetchMailboxDetails],
  );

  return {
    isLoading,
    error,
    mailboxes,
    messageTypes,
    defaultMailbox,
    mailboxSignature,
    defaultMessageType,
    populateReplyTemplate,
    changeMailbox,
  };
};
